import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, X, Plus, ShieldAlert, ShieldCheck, TriangleAlert, Info, ScanLine, Loader2, ShieldOff, UploadCloud } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, LeagueChip } from "@/components/badges";
import { relativeTime } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const INDEX = {
  green: { label: "Clean Slip", color: "#00E5A3", icon: ShieldCheck, desc: "No open status concerns across your legs." },
  yellow: { label: "Monitor", color: "#FFB020", icon: TriangleAlert, desc: "One leg has an open status concern. Verify before locking in." },
  red: { label: "High Info-Gap", color: "#FF4D4D", icon: ShieldAlert, desc: "Multiple legs have open status concerns worth researching." },
};

const RISK_COLOR = { clear: "#00E5A3", caution: "#FFB020", warning: "#FF8C00", critical: "#FF4D4D" };

export default function ParlayScanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [legs, setLegs] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [importing, setImporting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const addLeg = (p) => {
    if (legs.some((l) => l.id === p.id)) return false;
    setLegs((prev) => [...prev, p]);
    return true;
  };
  const addLegFromSearch = (p) => { addLeg(p); setQuery(""); setResults([]); };
  const removeLeg = (id) => setLegs(legs.filter((l) => l.id !== id));

  const handleFile = useCallback(async (f) => {
    if (!f) return;
    if (!user) { toast.message("Sign in to import a bet slip"); navigate("/login"); return; }
    if (!f.type?.startsWith("image/")) { toast.error("Please use a PNG, JPEG or WEBP image"); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/parlay/import-slip", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const matched = data.legs.filter((l) => l.player_id);
      if (matched.length) {
        setLegs((prev) => {
          const ids = new Set(prev.map((p) => p.id));
          const add = matched
            .filter((m) => !ids.has(m.player_id))
            .map((m) => ({ id: m.player_id, name: m.matched_name, league: m.league, team_abbr: "", position: "" }));
          return [...prev, ...add];
        });
      }
      const msg = `Read ${data.total} legs · matched ${data.matched_count}`;
      data.unmatched.length ? toast.warning(`${msg}. Not found: ${data.unmatched.join(", ")}`) : toast.success(msg);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not read that slip");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }, [user, navigate]);

  const onImport = (e) => handleFile(e.target.files?.[0]);
  const onDrop = (e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); };

  useEffect(() => {
    const onPaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const it of items) {
        if (it.type && it.type.startsWith("image/")) {
          const f = it.getAsFile();
          if (f) { handleFile(f); e.preventDefault(); break; }
        }
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [handleFile]);

  useEffect(() => {
    let active = true;
    const t = setTimeout(async () => {
      if (query.length < 2) { setResults([]); return; }
      const { data } = await api.get("/players", { params: { q: query, limit: 8 } });
      if (active) setResults(data);
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [query]);

  useEffect(() => {
    let active = true;
    const run = async () => {
      if (legs.length === 0) { setAnalysis(null); return; }
      try {
        const { data } = await api.post("/parlay/analyze", { legs: legs.map((l) => ({ player_id: l.id })) });
        if (active) setAnalysis(data);
      } catch {
        if (active) toast.error("Could not analyze slip");
      }
    };
    run();
    return () => { active = false; };
  }, [legs]);

  const idx = analysis ? INDEX[analysis.slip_health_index] : null;
  const IdxIcon = idx?.icon;

  return (
    <div className="max-w-[1100px] mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <p className="font-mono text-xs uppercase tracking-widest text-[#00E5A3] mb-1">Information Tool</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-100">Parlay Risk Scanner</h1>
        <p className="text-sm text-slate-400 mt-1">Add players to your slip. We flag open status concerns & information gaps — we never recommend bets.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 space-y-4">
          {/* import from screenshot */}
          <div
            data-testid="slip-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileRef.current?.click()}
            className={`rounded-xl border border-dashed p-5 cursor-pointer transition-colors ${dragOver ? "border-[#00E5A3] bg-[#00E5A3]/12" : "border-[#00E5A3]/40 bg-[#00E5A3]/5 hover:bg-[#00E5A3]/8"}`}
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-[#00E5A3]/12 flex items-center justify-center shrink-0">
                  {importing ? <Loader2 size={20} className="text-[#00E5A3] animate-spin" /> : <ScanLine size={20} className="text-[#00E5A3]" />}
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-100">{importing ? "Reading your slip…" : "Drop, paste or upload a bet slip"}</p>
                  <p className="text-xs text-slate-500">{user ? "Drag & drop, paste a screenshot (Ctrl/⌘ + V), or click to browse." : "Sign in to scan a slip — drag, paste or browse."}</p>
                </div>
              </div>
              <Button data-testid="slip-import-btn" onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }} disabled={importing}
                className="bg-[#00E5A3] hover:bg-[#00C98E] text-[#04140F] font-semibold h-9 rounded-lg">
                <UploadCloud size={15} className="mr-1.5" /> Browse
              </Button>
            </div>
            <p className="text-[11px] text-slate-500 flex items-center gap-1 mt-3"><ShieldOff size={11} /> Read by AI in the moment — your screenshot is never saved to our servers.</p>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="slip-file-input" onChange={onImport} />
          </div>

          {/* search */}
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input data-testid="parlay-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search a player to add a leg…"
              className="pl-9 bg-[#0F1420] border-[#232D42] text-slate-100 h-11" />
            {results.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-[#0F1420] border border-[#232D42] rounded-xl overflow-hidden shadow-2xl">
                {results.map((p) => (
                  <button key={p.id} data-testid={`parlay-result-${p.id}`} onClick={() => addLegFromSearch(p)}
                    className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[#1C2438] text-left">
                    <div className="flex items-center gap-2">
                      <LeagueChip league={p.league} />
                      <span className="text-slate-100 text-sm font-medium">{p.name}</span>
                      <span className="text-slate-500 text-xs font-mono">{p.team_abbr} · {p.position}</span>
                    </div>
                    <Plus size={15} className="text-[#00E5A3]" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* legs */}
          <div className="space-y-3" data-testid="parlay-legs">
            {legs.length === 0 && <div className="text-center py-12 text-slate-500 font-mono text-sm border border-dashed border-[#232D42] rounded-xl">Your slip is empty. Search & add players above.</div>}
            {analysis?.legs.map((leg) => (
              <div key={leg.player.id} data-testid={`parlay-leg-${leg.player.id}`}
                className="bg-[#0F1420] border rounded-xl p-4" style={{ borderColor: `${RISK_COLOR[leg.risk]}44` }}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <LeagueChip league={leg.player.league} />
                      <StatusBadge status={leg.status} />
                    </div>
                    <Link to={`/players/${leg.player.id}`} className="font-heading font-semibold text-slate-100 hover:text-[#00E5A3]">{leg.player.name}</Link>
                    <span className="text-slate-500 text-xs font-mono ml-2">{leg.player.team_abbr} · {leg.player.position}</span>
                    {leg.has_concern ? (
                      <p className="text-sm mt-2" style={{ color: RISK_COLOR[leg.risk] }}>⚠ {leg.status_note || "Open status concern"}</p>
                    ) : (
                      <p className="text-sm text-[#00E5A3] mt-2">✓ No open status concern</p>
                    )}
                    {leg.recent_events[0] && (
                      <p className="text-[11px] font-mono text-slate-500 mt-1">latest: {leg.recent_events[0].headline} · {relativeTime(leg.recent_events[0].timestamp_utc)}</p>
                    )}
                  </div>
                  <button data-testid={`parlay-remove-${leg.player.id}`} onClick={() => removeLeg(leg.player.id)} className="p-1.5 rounded-lg hover:bg-[#1C2438] text-slate-500 shrink-0">
                    <X size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Slip Health */}
        <div className="lg:col-span-5">
          <div className="bg-[#0F1420] border border-[#232D42] rounded-xl p-6 lg:sticky lg:top-20" data-testid="slip-health-panel">
            <h3 className="font-heading font-semibold text-slate-100 mb-4">Slip Health Index</h3>
            {!analysis ? (
              <p className="text-sm text-slate-500 font-mono">Add legs to compute your slip health.</p>
            ) : (
              <>
                <div className="flex items-center gap-3 rounded-xl p-4 mb-4" style={{ background: `${idx.color}12`, border: `1px solid ${idx.color}40` }}>
                  {IdxIcon && <IdxIcon size={28} style={{ color: idx.color }} />}
                  <div>
                    <p data-testid="slip-index-label" className="font-heading font-bold text-lg" style={{ color: idx.color }}>{idx.label}</p>
                    <p className="text-xs text-slate-400">{idx.desc}</p>
                  </div>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-400">Legs</span><span className="font-mono text-slate-200">{analysis.legs.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Legs with concerns</span>
                  <span data-testid="concern-count" className="font-mono font-bold" style={{ color: idx.color }}>{analysis.concern_count}</span>
                </div>
                <div className="mt-4 pt-4 border-t border-[#232D42] flex items-start gap-2">
                  <Info size={14} className="text-slate-500 shrink-0 mt-0.5" />
                  <p className="text-[11px] text-slate-500 leading-relaxed">{analysis.disclaimer}</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
